import * as React from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../reducers/reducers';
import { AppRouteComponentMatchParams, AppComponentProps } from '../../typescriptHelpers';
import styles from '../styles/settingsStyles';
import Text from 'antd/lib/typography/Text';
import Descriptions from 'antd/lib/descriptions';
import { AppStoreType, AutomationType, SettingType } from '../../types';
import Button from 'antd/lib/button';
import { Link, useHistory, useParams } from 'react-router-dom';
import Space from 'antd/lib/space';
import Paragraph from 'antd/lib/typography/Paragraph';
import { RunwayUserAction } from '../../helpers/aclHelpers';
import PermissionedActionButton from '../HOCs/PermissionedActionButton';
import { selectApp, selectAppStoreType } from '../../selectors/selectors';
import {
    createAppSettingsAutomationsRouteWithAutomation,
    createAppSettingsRouteWithSettingType,
} from '../../helpers/routeHelpers';

const ReleaseNoteDefaultsComponent: React.FC<AppComponentProps> = (props: AppComponentProps) => {
    const { appId } = props;
    const { appIdParam, orgIdParam } = useParams<AppRouteComponentMatchParams>();

    const history = useHistory();

    const appStoreDefaultReleaseNotes = useSelector((state: RootState) => {
        const app = selectApp(state, appId);

        return app.appSettings.appStoreDefaultReleaseNotes;
    });

    const appStoreType = useSelector((state: RootState) => selectAppStoreType(state, appId));

    const isAppleAppStore = appStoreType == AppStoreType.Apple;

    const hasAtLeastOneDefaultLocaleSet = appStoreDefaultReleaseNotes?.reduce((hasReleaseNotes, releaseNotes) => {
        if (hasReleaseNotes) {
            return hasReleaseNotes;
        } else {
            return !!releaseNotes.whatsNew;
        }
    }, false);
    const hasDefaultReleaseNotes = appStoreDefaultReleaseNotes?.length > 0 && hasAtLeastOneDefaultLocaleSet;

    const englishLocale =
        hasDefaultReleaseNotes ?
            appStoreDefaultReleaseNotes.find((releaseNotes) => releaseNotes.locale === 'en-US')
        :   null;
    const visibleLocale = hasDefaultReleaseNotes && englishLocale ? englishLocale : appStoreDefaultReleaseNotes[0];
    const remainingLanguagesCount = hasDefaultReleaseNotes ? appStoreDefaultReleaseNotes.length - 1 : null;

    let displayReleaseNotesLabelText;

    if (isAppleAppStore) {
        displayReleaseNotesLabelText = "'What's new in this version'";
    } else {
        displayReleaseNotesLabelText = 'Version release notes';
    }

    const onClickGoToDefaults = () => {
        const defaultsRoute = createAppSettingsRouteWithSettingType(
            orgIdParam,
            appIdParam,
            SettingType.DefaultReleaseNotes,
        );

        history.push(defaultsRoute);
    };

    const whatsNewTextOrReleaseNotesText = isAppleAppStore ? "'What's new' text" : 'release notes';

    const automationsRoute = createAppSettingsAutomationsRouteWithAutomation(
        orgIdParam,
        appIdParam,
        AutomationType.AutoApplyMetadata,
    );

    return (
        <div>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
                    <Text style={styles.appSettingsSectionTitleStyle}>Default {whatsNewTextOrReleaseNotesText}</Text>
                    <PermissionedActionButton
                        actionType={RunwayUserAction.UAUpdateAppSettings}
                        onClick={onClickGoToDefaults}
                    >
                        Edit metadata defaults
                    </PermissionedActionButton>
                </div>
                <Text type="secondary">
                    Set default {whatsNewTextOrReleaseNotesText} for every locale you need to support. Runway can
                    automatically apply those defaults to new versions with one of its{' '}
                    <Link to={automationsRoute}>automations</Link>.
                </Text>
                <Descriptions
                    bordered
                    column={1}
                    style={{ marginTop: 24, marginBottom: 12 }}
                    labelStyle={{ width: 240 }}
                >
                    <Descriptions.Item label={displayReleaseNotesLabelText}>
                        {hasDefaultReleaseNotes && (
                            <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                <Paragraph ellipsis={{ rows: 2, expandable: true }} style={{ marginBottom: 0 }}>
                                    <Text strong>{visibleLocale.displayLocale}: </Text>
                                    {visibleLocale.whatsNew ?? 'No default release notes set.'}
                                </Paragraph>
                                {appStoreDefaultReleaseNotes.length > 1 && (
                                    <div style={{ display: 'flex' }}>
                                        <Button type="link" onClick={onClickGoToDefaults} style={{ padding: 0 }}>
                                            ... and {remainingLanguagesCount} more{' '}
                                            {remainingLanguagesCount > 1 ? 'languages' : 'language'}
                                        </Button>
                                    </div>
                                )}
                            </Space>
                        )}
                        {!hasDefaultReleaseNotes && <Text type="secondary">No default release notes set</Text>}
                    </Descriptions.Item>
                </Descriptions>
            </div>
        </div>
    );
};

export default ReleaseNoteDefaultsComponent;
